/**
 * CoiBoundaryMap: renders a COI polygon on a lightweight MapLibre map.
 *
 * Same pattern as the AI sketch preview: an empty basemap style with the
 * boundary drawn as a fill + outline, fitted to the polygon bounds. The
 * map is interactive (pan/zoom) but has no drawing tools.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

const SOURCE_ID = 'coi-boundary-source';
const FILL_ID = 'coi-boundary-fill';
const LINE_ID = 'coi-boundary-line';
/** Boundary stroke/fill color, shared with the map legend. */
export const COI_BOUNDARY_COLOR = '#7B3F8C';

function emptyStyle(): maplibregl.StyleSpecification {
  return {
    version: 8,
    sources: {},
    layers: [{ id: 'background', type: 'background', paint: { 'background-color': '#eef2f7' } }],
  } as maplibregl.StyleSpecification;
}

function polygonBounds(polygon: Record<string, unknown>): maplibregl.LngLatBounds | null {
  const coords: [number, number][] = [];
  const walk = (c: unknown): void => {
    if (Array.isArray(c) && typeof c[0] === 'number') {
      const pair = c as [number, number];
      if (Number.isFinite(pair[0]) && Number.isFinite(pair[1])) coords.push(pair);
    } else if (Array.isArray(c)) {
      c.forEach(walk);
    }
  };
  walk((polygon as { coordinates?: unknown }).coordinates);
  if (coords.length === 0) return null;
  const bounds = new maplibregl.LngLatBounds(coords[0], coords[0]);
  for (const pair of coords) bounds.extend(pair);
  return bounds;
}

export function CoiBoundaryMap({
  polygon,
  className,
}: {
  polygon: Record<string, unknown>;
  className?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || mapRef.current) return;
    const map = new maplibregl.Map({
      container,
      style: emptyStyle(),
      center: [-122.24, 37.78],
      zoom: 10,
      attributionControl: false,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const data = {
      type: 'Feature',
      properties: {},
      geometry: polygon,
    } as unknown as GeoJSON.Feature;
    const apply = (): void => {
      const existing = map.getSource(SOURCE_ID);
      if (existing && existing.type === 'geojson') {
        (existing as maplibregl.GeoJSONSource).setData(data);
      } else {
        map.addSource(SOURCE_ID, { type: 'geojson', data });
        map.addLayer({
          id: FILL_ID,
          type: 'fill',
          source: SOURCE_ID,
          paint: { 'fill-color': COI_BOUNDARY_COLOR, 'fill-opacity': 0.25 },
        });
        map.addLayer({
          id: LINE_ID,
          type: 'line',
          source: SOURCE_ID,
          paint: { 'line-color': COI_BOUNDARY_COLOR, 'line-width': 2.5 },
        });
      }
      const bounds = polygonBounds(polygon);
      if (bounds) map.fitBounds(bounds, { padding: 32, maxZoom: 14 });
    };
    if (map.isStyleLoaded()) apply();
    else void map.once('load', apply);
  }, [polygon]);

  return <div ref={containerRef} className={className} role="img" aria-label={t('map.coiMapAria')} />;
}
